import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { PesaJetLogo } from './PesaJetLogo';
import { trapFocus, addKeyboardNavigation } from '../utils/accessibility';
import {
  LayoutDashboard,
  ArrowRightLeft,
  ShieldCheck,
  Key,
  Settings,
  FileText,
  Link2,
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: <LayoutDashboard className="h-6 w-6" />,
  },
  {
    name: 'Transactions',
    href: '/transactions',
    icon: <ArrowRightLeft className="h-6 w-6" />,
  },
  {
    name: 'Payment Links',
    href: '/payment-links',
    icon: <Link2 className="h-6 w-6" />,
  },
  {
    name: 'KYC Verification',
    href: '/kyc',
    icon: <ShieldCheck className="h-6 w-6" />,
  },
  {
    name: 'API Keys',
    href: '/api-keys',
    icon: <Key className="h-6 w-6" />,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: <Settings className="h-6 w-6" />,
  },
  {
    name: 'Documentation',
    href: '/docs',
    icon: <FileText className="h-6 w-6" />,
  },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  useEffect(() => {
    if (isOpen && sidebarRef.current) {
      const removeFocusTrap = trapFocus(sidebarRef.current);
      return removeFocusTrap;
    }
  }, [isOpen]);

  useEffect(() => {
    if (navRef.current) {
      const removeKeyboardNav = addKeyboardNavigation(navRef.current, {
        selector: 'a[role="menuitem"]',
        orientation: 'vertical',
        wrap: true,
      });
      return removeKeyboardNav;
    }
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div
          className="relative z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} aria-hidden="true" />
          <div className="fixed inset-0 flex">
            <div ref={sidebarRef} className="relative mr-16 flex w-full max-w-xs flex-1">
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button
                  type="button"
                  className="-m-2.5 p-2.5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-900 rounded-md"
                  onClick={onClose}
                  aria-label="Close navigation menu"
                >
                  <span className="sr-only">Close sidebar</span>
                  <svg
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <SidebarContent isActive={isActive} onLinkClick={onClose} navRef={navRef} />
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <SidebarContent isActive={isActive} navRef={navRef} />
      </div>
    </>
  );
}

interface SidebarContentProps {
  isActive: (href: string) => boolean;
  onLinkClick?: () => void;
  navRef?: React.RefObject<HTMLElement | null>;
}

function SidebarContent({ isActive, onLinkClick, navRef }: SidebarContentProps) {
  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-[#11131a] px-5 pb-5 text-white scrollbar-thin">
      <div className="flex h-24 shrink-0 items-center px-2">
        <PesaJetLogo size="md" variant="full" className="!text-white" />
      </div>

      <div className="border-t border-white/10"></div>

      <nav
        ref={navRef}
        className="flex flex-1 flex-col"
        role="navigation"
        aria-label="Main navigation"
      >
        <ul role="menu" className="flex flex-1 flex-col gap-y-7">
          <li role="none">
            <p className="px-3 pb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/35">
              Workspace
            </p>
            <ul role="none" className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name} role="none">
                  <Link
                    to={item.href}
                    onClick={onLinkClick}
                    role="menuitem"
                    aria-current={isActive(item.href) ? 'page' : undefined}
                    className={`nav-link ${
                      isActive(item.href)
                        ? 'nav-link-active bg-[#6d3af2] text-white shadow-lg shadow-black/20'
                        : 'text-white/55 hover:text-white hover:bg-white/7'
                    } group flex gap-x-3 rounded-xl px-3.5 py-3 text-sm leading-6 font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#c8ff4a] focus:ring-offset-2 focus:ring-offset-[#11131a]`}
                  >
                    <span
                      className={`h-5 w-5 shrink-0 transition-colors duration-200 ${
                        isActive(item.href)
                          ? 'text-[#c8ff4a]'
                          : 'text-white/40 group-hover:text-white'
                      }`}
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </li>

          <li className="mt-auto">
            <div className="rounded-2xl bg-white/5 p-4 border border-white/10">
              <div className="flex items-center">
                <div className="h-9 w-9 rounded-xl bg-[#c8ff4a] flex items-center justify-center">
                  <ShieldCheck className="h-4 w-4 text-[#11131a]" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-semibold text-white">Secure workspace</p>
                  <p className="text-[10px] text-white/40">All systems protected</p>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );
}
